/**
 * @file useAsync — generic async state hook. Keeps loading/error/data handling
 * out of components and consistent across every page.
 */

import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * @template T
 * @typedef {Object} AsyncState
 * @property {T|null} data
 * @property {boolean} loading
 * @property {Error|null} error
 * @property {() => void} reload
 */

/**
 * Run an async loader and expose typed {loading, error, data, reload}.
 * @template T
 * @param {() => Promise<T>} loader
 * @param {ReadonlyArray<*>} deps  Re-runs when these change.
 * @returns {AsyncState<T>}
 */
export function useAsync(loader, deps = []) {
  const [data, setData] = useState(/** @type {T|null} */ (null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error|null} */ (null));
  const [nonce, setNonce] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    Promise.resolve()
      .then(loader)
      .then((result) => {
        if (active && mounted.current) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active && mounted.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}
